if (!customElements.get('annadea-gallery-nav')) {
  customElements.define(
    'annadea-gallery-nav',
    class AnnadeaGalleryNav extends HTMLElement {
      constructor() {
        super();
        this.SWIPE_THRESHOLD = 40;

        this.handlePrev = () => this.step(-1);
        this.handleNext = () => this.step(1);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
      }

      connectedCallback() {
        // The custom element may upgrade before media-gallery.js has defined
        // setActiveMedia; closest() only needs the element to exist in the DOM.
        this.gallery = this.closest('media-gallery');
        if (!this.gallery) return;

        this.viewer = this.gallery.querySelector('.annadea-product-gallery__viewer');
        this.prevButton = this.querySelector('[name="previous"]');
        this.nextButton = this.querySelector('[name="next"]');

        this.prevButton?.addEventListener('click', this.handlePrev);
        this.nextButton?.addEventListener('click', this.handleNext);

        // Touch-only swipe (mobile). No pointer/mouse events -> desktop click-drag never navigates.
        if (this.viewer) {
          this.viewer.addEventListener('touchstart', this.handleTouchStart, { passive: true });
          this.viewer.addEventListener('touchmove', this.handleTouchMove, { passive: true });
          this.viewer.addEventListener('touchend', this.handleTouchEnd, { passive: true });
        }

        // Enable the crossfade only after JS loads so the initial LCP image never animates.
        this.gallery.classList.add('annadea-product-gallery--fade');
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.handlePrev);
        this.nextButton?.removeEventListener('click', this.handleNext);

        if (this.viewer) {
          this.viewer.removeEventListener('touchstart', this.handleTouchStart);
          this.viewer.removeEventListener('touchmove', this.handleTouchMove);
          this.viewer.removeEventListener('touchend', this.handleTouchEnd);
        }
      }

      get items() {
        return Array.from(this.gallery.querySelectorAll('.annadea-product-gallery__viewer-item'));
      }

      step(direction) {
        const items = this.items;
        if (items.length < 2) return;

        let currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
        if (currentIndex === -1) currentIndex = 0;

        const nextItem = items[(currentIndex + direction + items.length) % items.length];
        if (nextItem) this.gallery.setActiveMedia(nextItem.dataset.mediaId, false);
      }

      handleTouchStart(event) {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }

      handleTouchMove(event) {
        const touch = event.changedTouches[0];
        this.touchEndX = touch.clientX;
        this.touchEndY = touch.clientY;
      }

      handleTouchEnd() {
        if (this.touchStartX == null || this.touchEndX == null) return;

        const deltaX = this.touchEndX - this.touchStartX;
        const deltaY = this.touchEndY - this.touchStartY;

        // Only treat as a swipe when horizontal movement is dominant and past the threshold.
        // A tap (no movement) falls through untouched so the zoom-modal tap still fires.
        if (Math.abs(deltaX) > this.SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.step(deltaX < 0 ? 1 : -1);
        }

        this.touchStartX = this.touchEndX = this.touchStartY = this.touchEndY = null;
      }
    }
  );
}

/*
  Inline hosted-video slide for the ANNADEA PDP gallery.
  Renders a muted, looping <video> that autoplays when its slide is active (no native controls),
  with a branded centre play/pause button. Only the active slide's video plays; navigating away
  pauses it. Clicking/tapping the video (or the button) toggles playback. Respects reduced motion.
*/
if (!customElements.get('annadea-product-video')) {
  customElements.define(
    'annadea-product-video',
    class AnnadeaProductVideo extends HTMLElement {
      constructor() {
        super();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.handleClick = this.handleClick.bind(this);
        this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
        this.syncPlayingState = this.syncPlayingState.bind(this);
        this.syncToActive = this.syncToActive.bind(this);
      }

      connectedCallback() {
        this.video = this.querySelector('video');
        this.button = this.querySelector('.annadea-product-video__toggle');
        this.slide = this.closest('.annadea-product-gallery__viewer-item');
        if (!this.video) return;

        // Property (not just attribute) muting is required for muted autoplay on iOS Safari.
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');
        // Gallery videos always loop, independent of the section's enable_video_looping setting.
        this.video.loop = true;

        this.video.addEventListener('loadedmetadata', this.handleLoadedMetadata);
        this.video.addEventListener('play', this.syncPlayingState);
        this.video.addEventListener('pause', this.syncPlayingState);
        // One handler on the host covers clicks on the video surface and on the button (bubbles up),
        // plus keyboard Enter/Space on the focusable button.
        this.addEventListener('click', this.handleClick);

        // React to this slide becoming (in)active. media-gallery.js toggles `is-active` and then
        // calls pauseAllMedia(); this observer fires on the following microtask and re-plays the
        // now-active slide, so only one video ever plays at a time — with no base-JS edits.
        if (this.slide) {
          this.observer = new MutationObserver(this.syncToActive);
          this.observer.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
        }

        this.syncPlayingState();
        this.syncToActive();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.removeEventListener('click', this.handleClick);
        if (this.video) {
          this.video.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
          this.video.removeEventListener('play', this.syncPlayingState);
          this.video.removeEventListener('pause', this.syncPlayingState);
        }
      }

      get isActive() {
        // A lone slide (no is-active toggling) is shown via CSS :only-child — treat it as active.
        return !this.slide || this.slide.classList.contains('is-active');
      }

      handleLoadedMetadata() {
        // Load the true first frame through the video pipeline so a paused video shows a matching
        // still (avoids the JPEG-poster brightness pop).
        try {
          this.video.currentTime = 0;
        } catch (e) {
          /* seeking can throw before the video is seekable; harmless */
        }
      }

      handleClick(event) {
        if (this.video.paused) this.play();
        else this.video.pause();
        // After a pointer click, drop focus so the hover-only pause control doesn't stay stuck
        // once playback resumes. Keyboard-triggered clicks (event.detail === 0) keep focus so the
        // control stays visible for keyboard users.
        if (event && event.detail !== 0) this.button?.blur();
      }

      play() {
        this.video.muted = true;
        const attempt = this.video.play();
        // If the browser blocks playback (e.g. iOS Low Power Mode), the branded play button stays
        // visible so the user can start it manually.
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }

      playIfAllowed() {
        if (this.reducedMotion.matches) {
          this.video.pause();
          return;
        }
        this.play();
      }

      syncToActive() {
        if (this.isActive) this.playIfAllowed();
        else this.video.pause();
      }

      syncPlayingState() {
        const playing = !this.video.paused;
        this.classList.toggle('is-playing', playing);
        this.classList.toggle('is-paused', !playing);
        if (this.button) {
          const label = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
          if (label) this.button.setAttribute('aria-label', label);
        }
      }
    }
  );
}
